import { useForm } from '@tanstack/react-form';
import isURL from 'validator/es/lib/isURL';
import { z } from 'zod';

import { trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { tGlobal, tNamespace } from '../../services/translation';
import { parseFirstZodError } from '../../shared/validation';
import { Button } from '../buttons/button';
import { DialogFooter } from '../dialog';
import { FieldsDialog } from '../fields-dialog';
import { TextField } from '../inputs/text-field';
import { SwitchField } from '../switch';

const t = tNamespace('app.content.-editor.add-link');

export type AddLinkProps = {
  open: boolean;
  onOpenChange: (open: boolean) => unknown;
  onConfirm: (text: string | null, link: string, isTargetBlank: boolean) => unknown;
  selectedLink: { text: string; link: string } | null;
  onUnsetLink: () => unknown;
};

export function AddLink(props: AddLinkProps) {
  const { open, onOpenChange, onConfirm, selectedLink } = props;
  const form = useForm({
    defaultValues: {
      text: selectedLink?.text ?? null,
      url: selectedLink?.link ?? '',
      isTargetBlank: false,
    },
    onSubmit: function ({ value: { text, url, isTargetBlank } }) {
      // This is a workaround to allow relative URLs, setLink from tiptap seems to not work with relative URL when protocol is not present
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      onConfirm(text, url, isTargetBlank);
    },
  });

  return (
    <FieldsDialog title={t('title')} open={open} onOpenChange={onOpenChange}>
      <form
        className="grid grid-flow-row gap-4"
        onSubmit={function (e) {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit().catch(function (err) {
            captureException(
              err,
              {
                file: 'content/-editor/add-link.tsx',
                method: 'form.handleSubmit',
                infoMessage: 'unable to send form',
              },
              trackError
            );
          });
        }}
      >
        {selectedLink?.text == null || selectedLink?.text.trim() === '' ? (
          <form.Field
            name="text"
            validators={{
              onSubmit: function (data) {
                const parsed = z.string().min(1).safeParse(data.value);
                if (!parsed.success) {
                  return 'FIELD_REQUIRED';
                }
                return null;
              },
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  label={t('text')}
                  placeholder={t('textPlaceholder')}
                  sizeField="lg"
                  name={field.name}
                  value={field.state.value ?? ''}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  error={field.state.meta.errors}
                />
              );
            }}
          />
        ) : null}
        <form.Field
          name="url"
          validators={{
            onSubmit: function (data) {
              return parseFirstZodError(
                z.string().refine(isURL, {
                  message: 'FIELD_INVALID_URL',
                }),
                data.value
              );
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={function (field) {
            return (
              <TextField
                label={t('url')}
                placeholder={t('urlPlaceholder')}
                sizeField="lg"
                name={field.name}
                value={field.state.value}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                error={field.state.meta.errors}
              />
            );
          }}
        />
        <form.Field
          name="isTargetBlank"
          // eslint-disable-next-line react/no-children-prop
          children={function (field) {
            return (
              <SwitchField
                label={t('openLinkInNewTab')}
                onCheckedChange={function (value) {
                  field.handleChange(value);
                }}
                checked={field.state.value}
              />
            );
          }}
        />
        <DialogFooter>
          {selectedLink?.link == null || selectedLink.link.trim().length === 0 ? null : (
            <Button variant="ghost-accent" size="xl" className="w-full" onClick={props.onUnsetLink}>
              {t('unsetLink')}
            </Button>
          )}
          <Button
            className="w-full"
            variant="outline"
            size="xl"
            onClick={function () {
              onOpenChange(false);
            }}
          >
            {tGlobal('cancel')}
          </Button>
          <form.Subscribe
            selector={function (state) {
              return [state.canSubmit, state.isSubmitting];
            }}
            // eslint-disable-next-line react/no-children-prop
            children={function (data) {
              const [canSubmit, isSubmitting] = data;
              return (
                <Button
                  className="w-full"
                  variant="accent"
                  size="xl"
                  type="submit"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                >
                  {tGlobal('save')}
                </Button>
              );
            }}
          />
        </DialogFooter>
      </form>
    </FieldsDialog>
  );
}
